import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { runCheckout } from "../lib/payments";
import { useAuth } from "../context/AuthContext";
import { Check, Loader2, ShieldCheck, Lock } from "lucide-react";

export default function Checkout() {
  const { user, login } = useAuth();
  const [pricing, setPricing] = useState(null);
  const [paying, setPaying] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) { navigate("/start"); return; }
    api.pricing(user.country_code).then(setPricing).catch(() => {});
  }, [user, navigate]);

  const pay = async () => {
    setPaying(true);
    try {
      const { order } = await api.checkout("subscription");
      const payload = await runCheckout(order, user);
      const { user: updated } = await api.confirm(order.order_id, payload);
      login({ ...updated, token: user.token });
      toast.success("You're all set!");
      navigate("/assessment");
    } catch (err) {
      if (err?.message === "cancelled") { toast.info("Payment cancelled"); }
      else toast.error(err?.response?.data?.detail || "Payment could not be completed");
    } finally {
      setPaying(false);
    }
  };

  if (!pricing) return null;

  return (
    <div className="max-w-lg mx-auto px-5 py-16 fade-up">
      <h1 className="font-display text-3xl font-bold text-[#2B2625] mb-2">Your RelateMetrics pass</h1>
      <p className="text-[#6E655F] mb-8">One simple price. Everything you need to begin.</p>

      <div className="bg-white border border-[#E8E1D9] rounded-2xl p-7 shadow-sm">
        <div className="flex items-baseline justify-between pb-5 border-b border-[#E8E1D9]">
          <span className="font-semibold text-[#2B2625]">RelateMetrics assessment pass</span>
          <span className="font-display text-4xl font-bold text-[#6B21A8]" data-testid="checkout-price">
            {pricing.subscription_display}
          </span>
        </div>
        <ul className="space-y-3 py-5">
          {[
            "Your full 120-question assessment",
            "A warm 2-page personal report (PDF to your inbox)",
            "1 free invite for someone you choose",
            "A shared compatibility report together",
          ].map((t) => (
            <li key={t} className="flex items-start gap-3 text-[#2B2625]">
              <span className="mt-0.5 w-5 h-5 rounded-full bg-[#F2E9FA] text-[#3F3A44] grid place-items-center shrink-0">
                <Check size={13} />
              </span>
              <span className="text-sm">{t}</span>
            </li>
          ))}
        </ul>
        <button
          data-testid="checkout-pay" onClick={pay} disabled={paying}
          className="w-full inline-flex items-center justify-center gap-2 bg-[#6B21A8] hover:bg-[#4C1D95] text-white font-semibold py-3.5 rounded-xl transition-colors active:scale-[0.98] disabled:opacity-60"
        >
          {paying ? <Loader2 className="animate-spin" size={18} /> : <><Lock size={16} /> Pay {pricing.subscription_display}</>}
        </button>
        <p className="flex items-center justify-center gap-1.5 text-xs text-[#918780] mt-4">
          <ShieldCheck size={13} /> Secure checkout · Powered by Razorpay (Test Mode)
        </p>
      </div>
    </div>
  );
}
